import { ScreenClassProvider } from '@vp/swan';
import { useSwanStyleKeys } from '@vp/ubik-context';
import { ORDER_HISTORY_SWAN_STYLES } from '../constants/swan';
import { useMemo } from 'react';
import { SiteProvider } from './contexts/SiteProvider';
import { LocalizationProvider } from '@vp/om-translations';
import { ExperimentsProvider } from './contexts/ExperimentsContext';
import type { SiteData } from '../types/siteData.types';
import { PageModsProvider } from './contexts/PageModsProvider';
import { UserFeatureProvider } from '@vp/core-ui-components';
import type { PageMod } from '../types/mod.types';
import { OrderHistorySession } from './OrderHistorySession';
import { DebugModeProvider } from './contexts/DebugModeContext';
import { InitializeNewRelic } from '../newrelic/InitializeNewRelic';
import { InboundQueryStringContextProvider } from '@vp/common-ubik-utils';

export interface Props {
  locale: string;
  tenant?: string;
  environment?: string;
  experiments: { [key: string]: string | undefined } | undefined;
  siteData: SiteData;
  localizations: object;
  featureFlags: { [key: string]: string | undefined } | undefined;
}

export const Fragment = (props: Props) => {
  const { locale, siteData, localizations } = props;
  console.log('Rendering order history fragment with props::', { ...props });
  const { uxMods: rawSiteUxMods, featureControls } = siteData;
  // because gatsby can't pass through objects with 'id' properties, our source json uses 'modId'.
  // we need to map those modRefs into the format that mod-father requires.
  const siteConfigPageUxMods: PageMod[] = (rawSiteUxMods || []).map(
    ({ modRef: { family, modId }, scope }) => ({
      modRef: {
        family,
        id: modId,
      },
      scope,
      enabled: true,
    })
  );

  const siteContextValues = useMemo(
    () => ({
      ...siteData,
    }),
    [siteData]
  );

  useSwanStyleKeys(ORDER_HISTORY_SWAN_STYLES);

  return (
    <>
      <InitializeNewRelic />
      <SiteProvider value={siteContextValues}>
        <ScreenClassProvider>
          <LocalizationProvider
            translationResources={localizations}
            locale={locale}
            logLevel="ERROR"
          >
            <InboundQueryStringContextProvider>
              <DebugModeProvider>
                <ExperimentsProvider>
                  <PageModsProvider pageUxMods={siteConfigPageUxMods}>
                    <UserFeatureProvider featureControls={featureControls}>
                      <OrderHistorySession />
                    </UserFeatureProvider>
                  </PageModsProvider>
                </ExperimentsProvider>
              </DebugModeProvider>
            </InboundQueryStringContextProvider>
          </LocalizationProvider>
        </ScreenClassProvider>
      </SiteProvider>
    </>
  );
};
